import { useState, useMemo, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap, LayersControl, Polyline } from 'react-leaflet'
import { DivIcon, LatLngExpression } from 'leaflet'
import { RefreshCw, Wifi, WifiOff, Layers, Search, X, Loader2 } from 'lucide-react'
import { getMapRadios, getRadioDetails, syncRadio, Radio, runGpsAudit, getGpsAuditStatus, isCustomerBlocked } from '../services/api'
import GpsAuditLayer from '../components/GpsAuditLayer'
import { formatDistanceToNow } from 'date-fns'
import { useRole } from '../lib/RoleContext'
import { hasRole } from '../lib/role'

type FilterType = 'connected' | 'disconnected' | 'needs_attention'

// Create custom marker icons based on status for RN (Remote Node)
// 5 GHz = green, 6 GHz = cyan when connected
const createMarkerIcon = (status: string, connected: boolean, frequencyBand: string | null, blocked = false): DivIcon => {
  let color = '#9CA3AF' // gray for disconnected

  if (blocked) {
    // Highest precedence: Splynx account blocked/disabled → dark red, even if connected.
    color = '#991B1B'
  } else if (connected) {
    switch (status) {
      case 'connected':
        // Different colors for 5 GHz vs 6 GHz
        color = frequencyBand?.includes('6') ? '#06B6D4' : '#22C55E' // cyan for 6 GHz, green for 5 GHz
        break
      case 'needs_attention':
        color = '#F59E0B' // yellow/orange
        break
      case 'pending':
        color = '#F59E0B' // yellow
        break
      case 'error':
        color = '#EF4444' // red
        break
      case 'rogue':
        color = '#A855F7' // purple
        break
    }
  }

  return new DivIcon({
    className: 'custom-marker',
    html: `
      <div style="
        width: 12px;
        height: 12px;
        background-color: ${color};
        border: 1.5px solid white;
        border-radius: 50%;
        box-shadow: 0 1px 3px rgba(0,0,0,0.3);
      "></div>
    `,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
    popupAnchor: [0, -6],
  })
}

// Create wedge/pie marker icon for BN (Base Node) pointing at azimuth
const createBNMarkerIcon = (azimuth: number, connected: boolean): DivIcon => {
  // Orange color for BN, gray if disconnected
  const color = connected ? '#F97316' : '#6B7280'

  // SVG wedge (90-degree pie slice) covering upper-right quadrant (NE direction)
  // Azimuth: 0° = North, 90° = East, 180° = South, 270° = West
  // The wedge naturally points at 45° (NE), so subtract 45° to align with azimuth
  const rotation = azimuth - 45

  return new DivIcon({
    className: 'bn-marker',
    html: `
      <div style="
        width: 32px;
        height: 32px;
        transform: rotate(${rotation}deg);
        filter: drop-shadow(0 2px 3px rgba(0,0,0,0.3));
      ">
        <svg viewBox="0 0 32 32" width="32" height="32">
          <!-- Wedge path: 90-degree pie slice pointing right -->
          <path
            d="M16 16 L32 16 A16 16 0 0 0 16 0 Z"
            fill="${color}"
            stroke="white"
            stroke-width="2"
          />
          <!-- Center dot -->
          <circle cx="16" cy="16" r="4" fill="white" />
        </svg>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
  })
}

// Compass Rose component - positioned in bottom right of map
function CompassRose() {
  return (
    <div
      className="absolute bottom-4 right-4 z-[1000] pointer-events-none"
      style={{ width: '80px', height: '80px' }}
    >
      <svg viewBox="0 0 100 100" width="80" height="80">
        {/* Background circle */}
        <circle cx="50" cy="50" r="48" fill="rgba(255,255,255,0.85)" stroke="#374151" strokeWidth="1.5" />

        {/* Cardinal direction letters */}
        <text x="50" y="18" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#1F2937">N</text>
        <text x="50" y="92" textAnchor="middle" fontSize="12" fill="#6B7280">S</text>
        <text x="8" y="54" textAnchor="middle" fontSize="12" fill="#6B7280">W</text>
        <text x="92" y="54" textAnchor="middle" fontSize="12" fill="#6B7280">E</text>

        {/* North arrow (red) */}
        <polygon points="50,22 45,50 50,45 55,50" fill="#DC2626" />
        {/* South arrow (gray) */}
        <polygon points="50,78 45,50 50,55 55,50" fill="#9CA3AF" />
        {/* East arrow (gray) */}
        <polygon points="78,50 50,45 55,50 50,55" fill="#9CA3AF" />
        {/* West arrow (gray) */}
        <polygon points="22,50 50,45 45,50 50,55" fill="#9CA3AF" />

        {/* Center dot */}
        <circle cx="50" cy="50" r="4" fill="#374151" />
        <circle cx="50" cy="50" r="2" fill="white" />
      </svg>
    </div>
  )
}

// Component to fit map to markers - only on initial load
function FitBounds({ radios }: { radios: Radio[] }) {
  const map = useMap()
  const [hasFitted, setHasFitted] = useState(false)

  useEffect(() => {
    if (hasFitted) return

    const validRadios = radios.filter(
      (r) => r.latitude && r.longitude && r.latitude !== 0 && r.longitude !== 0
    )

    if (validRadios.length > 0) {
      const bounds = validRadios.map(
        (r) => [r.latitude!, r.longitude!] as [number, number]
      )
      map.fitBounds(bounds, { padding: [50, 50] })
      setHasFitted(true)
    }
  }, [radios, map, hasFitted])

  return null
}

interface RadioPopupProps {
  radio: Radio
  onSync: (serial: string) => void
  role: string
}

function RadioPopup({ radio, onSync, role }: RadioPopupProps) {
  const [details, setDetails] = useState<Radio | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch details when popup opens
  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await getRadioDetails(radio.serial_number)
        setDetails(response.data)
      } catch (err) {
        setError('Failed to load details')
        console.error('Error fetching radio details:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchDetails()
  }, [radio.serial_number])

  // Use details if loaded, otherwise fall back to basic radio info
  const displayRadio = details || radio

  return (
    <div className="min-w-[220px]">
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold text-gray-900">{displayRadio.hostname || displayRadio.serial_number}</div>
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
          displayRadio.radio_type === 'BN' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
        }`}>
          {displayRadio.radio_type === 'BN' ? 'Base Node' : 'Remote Node'}
        </span>
      </div>
      <div className="space-y-1 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-gray-600">Serial:</span>
          <span className="font-mono text-xs">{displayRadio.serial_number}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-600">Status:</span>
          <span className="flex items-center">
            {displayRadio.connected ? (
              <Wifi className="h-4 w-4 text-green-500" />
            ) : (
              <WifiOff className="h-4 w-4 text-gray-400" />
            )}
          </span>
        </div>
        {displayRadio.radio_type === 'BN' && (
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Azimuth:</span>
            <span className="font-medium">{displayRadio.azimuth}°</span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-gray-600">Band:</span>
          <span className={`font-medium ${displayRadio.frequency_band?.includes('6') ? 'text-cyan-600' : 'text-green-600'}`}>
            {displayRadio.frequency_band || '-'}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-600">SLA:</span>
          <span className="font-medium">{displayRadio.sla_profile || 'N/A'}</span>
        </div>
        {displayRadio.customer_name && (
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Customer:</span>
            <span className="truncate max-w-[120px]">{displayRadio.customer_name}</span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-gray-600">Status:</span>
          <span
            className={`px-2 py-0.5 rounded text-xs font-medium ${
              displayRadio.needs_attention
                ? 'bg-yellow-100 text-yellow-700'
                : displayRadio.connected
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-700'
            }`}
          >
            {displayRadio.needs_attention ? 'Needs Attention' : displayRadio.connected ? 'Connected' : 'Disconnected'}
          </span>
        </div>

        {/* Signal metrics - only shown when details are loaded */}
        {loading ? (
          <div className="flex items-center justify-center py-2 text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            <span className="text-xs">Loading details...</span>
          </div>
        ) : error ? (
          <div className="text-xs text-red-500 py-1">{error}</div>
        ) : details && (
          <>
            {details.distance !== null && details.distance !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Distance:</span>
                <span className="font-mono text-xs">{(details.distance / 1000).toFixed(2)} km</span>
              </div>
            )}
            {details.pathloss !== null && details.pathloss !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Path Loss:</span>
                <span className="font-mono text-xs">{details.pathloss.toFixed(1)} dB</span>
              </div>
            )}
            {details.alignment_metric !== null && details.alignment_metric !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Alignment:</span>
                <span className="font-mono text-xs">{details.alignment_metric.toFixed(1)} dB</span>
              </div>
            )}
            {details.last_seen_at && (
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Last Seen:</span>
                <span className="text-xs">
                  {formatDistanceToNow(new Date(details.last_seen_at), { addSuffix: true })}
                </span>
              </div>
            )}
            {details.radio_type === 'RN' && details.primary_bn && (
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Primary BN:</span>
                <span className="font-mono text-xs">{details.primary_bn}</span>
              </div>
            )}
          </>
        )}
      </div>
      {displayRadio.radio_type === 'RN' && hasRole(role, 'admin') && (
        <button
          onClick={() => onSync(radio.serial_number)}
          className="mt-3 w-full flex items-center justify-center px-3 py-1.5 bg-primary-600 text-white text-sm rounded hover:bg-primary-700"
        >
          <RefreshCw className="h-3 w-3 mr-1" />
          Sync
        </button>
      )}
    </div>
  )
}

interface FilterButtonProps {
  filter: FilterType
  activeFilters: Set<FilterType>
  color: string
  label: string
  count: number
  onToggle: (filter: FilterType) => void
}

function FilterButton({ filter, activeFilters, color, label, count, onToggle }: FilterButtonProps) {
  const isActive = activeFilters.has(filter)
  return (
    <button
      onClick={() => onToggle(filter)}
      className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
        isActive
          ? 'bg-gray-800 text-white shadow-md'
          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
      }`}
    >
      <div
        className="w-3 h-3 rounded-full mr-2"
        style={{ backgroundColor: color }}
      ></div>
      <span>{label}</span>
      <span className={`ml-2 text-xs ${isActive ? 'text-gray-300' : 'text-gray-400'}`}>
        ({count})
      </span>
    </button>
  )
}

export default function MapView() {
  const role = useRole()
  // All filters enabled by default
  const [activeFilters, setActiveFilters] = useState<Set<FilterType>>(
    new Set(['connected', 'disconnected', 'needs_attention'])
  )
  const [search, setSearch] = useState('')
  const [hideBN, setHideBN] = useState(false)
  // Track highlighted RN for drawing connection line to BN
  const [highlightedRN, setHighlightedRN] = useState<string | null>(null)
  // Track if popup is open to prevent mouseout from clearing highlight
  const [popupOpen, setPopupOpen] = useState(false)

  const { data: radios, isLoading, error } = useQuery({
    queryKey: ['mapRadios'],
    queryFn: async () => {
      const response = await getMapRadios()
      return response.data
    },
  })

  const [auditDryRun, setAuditDryRun] = useState(true)
  const [auditMsg, setAuditMsg] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const { data: auditStatus } = useQuery({
    queryKey: ['gpsAuditStatus'],
    queryFn: async () => (await getGpsAuditStatus()).data,
    refetchInterval: (q) => (q.state.data?.running ? 5_000 : false),
  })

  const auditMutation = useMutation({
    mutationFn: () => runGpsAudit(auditDryRun),
    onSuccess: () => {
      setAuditMsg(null)
      queryClient.invalidateQueries({ queryKey: ['gpsAuditStatus'] })
    },
    onError: (err: any) =>
      setAuditMsg(err?.response?.data?.detail ?? 'audit failed to start'),
  })

  useEffect(() => {
    if (auditStatus && !auditStatus.running && auditStatus.run_id) {
      const t = auditStatus.tally
      setAuditMsg(`done: ${Object.entries(t).map(([k, v]) => `${k} ${v}`).join(', ')}`)
      queryClient.invalidateQueries({ queryKey: ['gpsAuditResults'] })
    }
  }, [auditStatus?.running])

  const handleSync = async (serial: string) => {
    try {
      await syncRadio(serial)
    } catch (err) {
      console.error('Sync failed:', err)
    }
  }

  const toggleFilter = (filter: FilterType) => {
    setActiveFilters(prev => {
      const next = new Set(prev)
      if (next.has(filter)) {
        next.delete(filter)
      } else {
        next.add(filter)
      }
      return next
    })
  }

  // Get connection line from RN to its BN
  const getConnectionLine = useCallback((): LatLngExpression[] | null => {
    if (!highlightedRN || !radios) return null

    const rn = radios.find(r => r.serial_number === highlightedRN)
    if (!rn || rn.radio_type !== 'RN' || !rn.connected_bn) return null
    if (!rn.latitude || !rn.longitude) return null

    const bn = radios.find(r => r.serial_number === rn.connected_bn)
    if (!bn || !bn.latitude || !bn.longitude) return null

    return [
      [rn.latitude, rn.longitude],
      [bn.latitude, bn.longitude]
    ]
  }, [highlightedRN, radios])

  const connectionLine = getConnectionLine()

  // Filter radios based on search and active filters
  const filteredRadios = useMemo(() => {
    if (!radios) return []

    let items = radios

    // Apply search filter first
    if (search) {
      const searchLower = search.toLowerCase()
      items = items.filter(r =>
        r.serial_number.toLowerCase().includes(searchLower) ||
        (r.hostname || '').toLowerCase().includes(searchLower) ||
        (r.customer_name || '').toLowerCase().includes(searchLower)
      )
    }

    // Apply BN filter
    if (hideBN) {
      items = items.filter(r => r.radio_type !== 'BN')
    }

    // Apply status filters
    items = items.filter(radio => {
      if (!radio.connected) {
        return activeFilters.has('disconnected')
      }
      if (radio.needs_attention) {
        return activeFilters.has('needs_attention')
      }
      return activeFilters.has('connected')
    })

    return items
  }, [radios, search, hideBN, activeFilters])

  // Calculate counts based on search-filtered results (before status filter)
  const searchFilteredRadios = useMemo(() => {
    if (!radios) return []
    if (!search) return radios

    const searchLower = search.toLowerCase()
    return radios.filter(r =>
      r.serial_number.toLowerCase().includes(searchLower) ||
      (r.hostname || '').toLowerCase().includes(searchLower) ||
      (r.customer_name || '').toLowerCase().includes(searchLower)
    )
  }, [radios, search])

  const counts = {
    connected: searchFilteredRadios.filter(r => r.connected && !r.needs_attention).length,
    disconnected: searchFilteredRadios.filter(r => !r.connected).length,
    needs_attention: searchFilteredRadios.filter(r => r.needs_attention).length,
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[600px] bg-white rounded-lg">
        <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Failed to load map data</p>
      </div>
    )
  }

  // Default center (Texas)
  const defaultCenter: [number, number] = [31.9686, -99.9018]

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Radio Map</h1>
        <div className="flex items-center space-x-4">
          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search serial, hostname..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-64 pl-10 pr-8 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <Layers className="h-4 w-4 text-gray-400 mr-1" />
            <FilterButton
              filter="connected"
              activeFilters={activeFilters}
              color="#22C55E"
              label="Connected"
              count={counts.connected}
              onToggle={toggleFilter}
            />
            <FilterButton
              filter="disconnected"
              activeFilters={activeFilters}
              color="#9CA3AF"
              label="Disconnected"
              count={counts.disconnected}
              onToggle={toggleFilter}
            />
            <FilterButton
              filter="needs_attention"
              activeFilters={activeFilters}
              color="#F59E0B"
              label="Needing Attention"
              count={counts.needs_attention}
              onToggle={toggleFilter}
            />
          </div>
          <div className="flex items-center ml-4 border-l pl-4 border-gray-300">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={hideBN}
                onChange={(e) => setHideBN(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-gray-700">Hide Base Nodes</span>
            </label>
          </div>
          {hasRole(role, 'admin') && (
            <div className="flex items-center ml-4 border-l pl-4 border-gray-300 space-x-2">
              <label className="flex items-center text-sm text-gray-600 space-x-1">
                <input type="checkbox" checked={auditDryRun} onChange={e => setAuditDryRun(e.target.checked)} />
                <span>dry run</span>
              </label>
              <button
                onClick={() => auditMutation.mutate()}
                disabled={auditStatus?.running || auditMutation.isPending}
                className="px-3 py-1 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {auditStatus?.running
                  ? `Auditing… ${auditStatus.processed}/${auditStatus.total}`
                  : 'Run GPS audit'}
              </button>
              {auditMsg && <span className="text-sm text-gray-600">{auditMsg}</span>}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div style={{ height: 'calc(100vh - 220px)', position: 'relative' }}>
          <CompassRose />
          <MapContainer
            center={defaultCenter}
            zoom={7}
            style={{ height: '100%', width: '100%' }}
          >
            <LayersControl position="topright">
              {/* Base Layers */}
              <LayersControl.BaseLayer checked name="OpenStreetMap">
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
              </LayersControl.BaseLayer>

              <LayersControl.BaseLayer name="Satellite">
                <TileLayer
                  attribution='&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                />
              </LayersControl.BaseLayer>

              <LayersControl.BaseLayer name="Satellite Hybrid">
                <TileLayer
                  attribution='&copy; Esri'
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                />
              </LayersControl.BaseLayer>

              <LayersControl.BaseLayer name="USGS Topo">
                <TileLayer
                  attribution='Tiles courtesy of the <a href="https://usgs.gov/">U.S. Geological Survey</a>'
                  url="https://basemap.nationalmap.gov/arcgis/rest/services/USGSTopo/MapServer/tile/{z}/{y}/{x}"
                  maxZoom={16}
                />
              </LayersControl.BaseLayer>

              <LayersControl.BaseLayer name="USGS Imagery">
                <TileLayer
                  attribution='Tiles courtesy of the <a href="https://usgs.gov/">U.S. Geological Survey</a>'
                  url="https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryOnly/MapServer/tile/{z}/{y}/{x}"
                  maxZoom={16}
                />
              </LayersControl.BaseLayer>

              <LayersControl.BaseLayer name="Terrain">
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                  url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                />
              </LayersControl.BaseLayer>

              <LayersControl.Overlay name="GPS Audit">
                <GpsAuditLayer />
              </LayersControl.Overlay>
            </LayersControl>

            {radios && <FitBounds radios={filteredRadios} />}
            {filteredRadios.map((radio) => {
              if (!radio.latitude || !radio.longitude || radio.latitude === 0) {
                return null
              }
              // Use wedge icon for BN (Base Node), circle for RN (Remote Node)
              // Determine status for marker color
              let status = 'offline'
              if (radio.connected) {
                status = radio.needs_attention ? 'needs_attention' : 'connected'
              }
              const icon = radio.radio_type === 'BN'
                ? createBNMarkerIcon(radio.azimuth ?? 0, radio.connected)
                : createMarkerIcon(status, radio.connected, radio.frequency_band, isCustomerBlocked(radio.customer_status))
              return (
                <Marker
                  key={radio.serial_number}
                  position={[radio.latitude, radio.longitude]}
                  icon={icon}
                  eventHandlers={{
                    mouseover: () => radio.radio_type === 'RN' && setHighlightedRN(radio.serial_number),
                    mouseout: () => !popupOpen && setHighlightedRN(null),
                    popupopen: () => {
                      setPopupOpen(true)
                      if (radio.radio_type === 'RN') setHighlightedRN(radio.serial_number)
                    },
                    popupclose: () => {
                      setPopupOpen(false)
                      setHighlightedRN(null)
                    },
                  }}
                >
                  <Tooltip
                    direction="top"
                    offset={[0, -10]}
                    opacity={0.95}
                    permanent={false}
                    sticky={false}
                  >
                    <div className="text-xs">
                      <div className="font-semibold">{radio.hostname || radio.serial_number}</div>
                      <div className="text-gray-600">
                        {radio.radio_type === 'BN' ? 'Base Node' : 'Remote Node'} • {radio.frequency_band} GHz
                      </div>
                      <div className={`font-medium ${
                        radio.needs_attention
                          ? 'text-yellow-600'
                          : radio.connected
                            ? 'text-green-600'
                            : 'text-gray-500'
                      }`}>
                        {radio.needs_attention ? 'Needs Attention' : radio.connected ? 'Connected' : 'Disconnected'}
                      </div>
                      {radio.sla_profile && <div>SLA: {radio.sla_profile}</div>}
                    </div>
                  </Tooltip>
                  <Popup>
                    <RadioPopup radio={radio} onSync={handleSync} role={role} />
                  </Popup>
                </Marker>
              )
            })}

            {/* Connection line from RN to BN */}
            {connectionLine && (
              <Polyline
                positions={connectionLine}
                pathOptions={{
                  color: '#F97316',
                  weight: 2,
                  dashArray: '8, 8',
                  opacity: 0.8,
                }}
              />
            )}
          </MapContainer>
        </div>
      </div>

      {/* Status summary */}
      <div className="text-sm text-gray-500 text-center">
        Showing {filteredRadios.length} of {radios?.length || 0} radios
        {search && (
          <span className="text-gray-400 ml-1">
            (matching "{search}")
          </span>
        )}
        {(activeFilters.size < 3 || search) && (
          <button
            onClick={() => {
              setActiveFilters(new Set(['connected', 'disconnected', 'needs_attention']))
              setSearch('')
            }}
            className="ml-2 text-primary-600 hover:text-primary-700 underline"
          >
            Clear filters
          </button>
        )}
      </div>
    </div>
  )
}
